import { validateHttpsUrl, validateRequired } from '@workspace/seedforge-shared'
import { Badge } from '@workspace/ui/components/badge'
import { Checkbox } from '@workspace/ui/components/checkbox'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import {
  SecondaryButton,
  secondaryLinkClasses,
} from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Globe,
  Link2,
  Loader2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { EditOverlay } from '../components/edit-overlay'
import { FieldLabel } from '../components/field-label'

// --- Types ---

export type PersonaStatus = 'ready' | 'processing' | 'failed' | 'none'

export interface EnumOption {
  value: string
  label: string
}

export interface InvestorProfileData {
  fundName: string
  fundDescription: string
  websiteUrl: string
  investmentAreas: string[]
  investmentGeographies: string[]
}

export interface PersonaData {
  personaStatus: PersonaStatus
  personaError?: string | null
  personaBrief?: string | null
}

export interface InvestorDashboardProps {
  /** Investor's name for greeting */
  name: string
  /** The investor's slug for the pitch link */
  slug: string | null
  /** Full pitch link URL for copying/preview */
  pitchLinkUrl: string
  /** Persona data from dedicated endpoint */
  persona?: PersonaData | null
  /** Fund name */
  fundName: string
  /** Fund description */
  fundDescription?: string
  /** Fund website URL */
  websiteUrl?: string | null
  /** Investment areas */
  investmentAreas?: string[]
  /** Investment geographies */
  investmentGeographies?: string[]
  /** Available investment area options for the checkbox UI */
  areaOptions?: EnumOption[]
  /** Available investment region options for the checkbox UI */
  regionOptions?: EnumOption[]
  /** Current thesis document file name, if uploaded */
  thesisFileName?: string | null
  /** Whether the profile section is editable */
  editable?: boolean
  /** Called when user saves profile edits inline */
  onProfileSave?: (data: InvestorProfileData) => void
  /** Called when user saves a new slug. Returns error message on failure, or void on success. */
  onSlugSave?: (slug: string) => Promise<string | undefined>
  /** Called when user drops/selects a thesis file */
  onThesisUpload?: (file: File) => void
}

// --- Components ---

function ProfileEditForm({
  fundName,
  fundDescription,
  websiteUrl,
  investmentAreas,
  investmentGeographies,
  areaOptions,
  regionOptions,
  onSave,
  onCancel,
}: {
  fundName: string
  fundDescription: string
  websiteUrl: string
  investmentAreas: string[]
  investmentGeographies: string[]
  areaOptions: EnumOption[]
  regionOptions: EnumOption[]
  onSave: (data: InvestorProfileData) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(fundName)
  const [description, setDescription] = useState(fundDescription)
  const [website, setWebsite] = useState(websiteUrl)
  const [selectedAreas, setSelectedAreas] = useState<string[]>(investmentAreas)
  const [selectedRegions, setSelectedRegions] = useState<string[]>(investmentGeographies)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({})

  const validateField = (field: string, value: string) => {
    let error: string | null = null
    if (field === 'name') error = validateRequired(value, 'Fund name')
    else if (field === 'website') error = validateHttpsUrl(value)
    setFieldErrors((prev) => ({ ...prev, [field]: error }))
    return error
  }

  const toggleArea = (value: string) => {
    setSelectedAreas((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const toggleRegion = (value: string) => {
    setSelectedRegions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  function handleSave() {
    const errors = {
      name: validateRequired(name, 'Fund name'),
      website: validateHttpsUrl(website),
    }
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return

    onSave({
      fundName: name,
      fundDescription: description,
      websiteUrl: website,
      investmentAreas: selectedAreas,
      investmentGeographies: selectedRegions,
    })
  }

  return (
    <div className="border-t border-[#f97316]/10 px-6 py-4 space-y-3">
      <div>
        <FieldLabel>Fund Name</FieldLabel>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => validateField('name', name)}
          className={cn('mt-1', fieldErrors.name && 'ring-destructive/50 ring-[1.5px]')}
        />
        {fieldErrors.name && <p className="mt-1 text-xs text-red-400">{fieldErrors.name}</p>}
      </div>
      <div>
        <FieldLabel>Description</FieldLabel>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="mt-1"
        />
      </div>
      <div>
        <FieldLabel>Website URL</FieldLabel>
        <Input
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          onBlur={() => validateField('website', website)}
          placeholder="https://example.com"
          className={cn('mt-1', fieldErrors.website && 'ring-destructive/50 ring-[1.5px]')}
        />
        {fieldErrors.website && <p className="mt-1 text-xs text-red-400">{fieldErrors.website}</p>}
      </div>
      <div>
        <FieldLabel>Investment Areas</FieldLabel>
        <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-2">
          {areaOptions.map((area) => (
            <button
              type="button"
              key={area.value}
              onClick={() => toggleArea(area.value)}
              className="flex items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
            >
              <Checkbox
                checked={selectedAreas.includes(area.value)}
                onCheckedChange={() => toggleArea(area.value)}
              />
              <span className="text-xs">{area.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <FieldLabel>Geographies</FieldLabel>
        <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-2">
          {regionOptions.map((region) => (
            <button
              type="button"
              key={region.value}
              onClick={() => toggleRegion(region.value)}
              className="flex items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
            >
              <Checkbox
                checked={selectedRegions.includes(region.value)}
                onCheckedChange={() => toggleRegion(region.value)}
              />
              <span className="text-xs">{region.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <SecondaryButton size="sm" icon={<X className="size-3.5" />} onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <CtaButton size="sm" icon={<Check className="size-3.5" />} onClick={handleSave}>
          Save
        </CtaButton>
      </div>
    </div>
  )
}

// --- Persona Status Card (standalone, above the link) ---

function PersonaStatusCard({
  personaStatus,
  personaBrief,
  onUploadThesis,
}: {
  personaStatus: PersonaStatus
  /** Opens the thesis upload: the way out of a failed persona. */
  onUploadThesis?: () => void
  personaBrief?: string | null
}) {
  if (personaStatus === 'ready') {
    return (
      <div className="rounded-xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/[0.06] via-emerald-500/[0.03] to-transparent px-6 py-5">
        <div className="flex items-center gap-2.5">
          <Sparkles className="size-4 text-emerald-500" />
          <h2 className="text-sm font-semibold text-foreground">Investor persona active</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {personaBrief ||
            'Your personalized investor persona is ready. Founders pitching through your link will be evaluated against your investment thesis.'}
        </p>
      </div>
    )
  }

  if (personaStatus === 'processing') {
    return (
      <div className="rounded-xl border border-amber-500/20 bg-gradient-to-r from-amber-500/[0.06] via-amber-500/[0.03] to-transparent px-6 py-5">
        <div className="flex items-center gap-2.5">
          <Loader2 className="size-4 animate-spin text-amber-500" />
          <h2 className="text-sm font-semibold text-foreground">Building your investor persona</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          We're analyzing your investment profile to create your personalized investor persona. This
          usually takes a few minutes.
        </p>
      </div>
    )
  }

  if (personaStatus === 'failed') {
    return (
      <div className="rounded-xl border border-red-500/20 bg-gradient-to-r from-red-500/[0.06] via-red-500/[0.03] to-transparent px-6 py-5">
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="size-4 text-red-500" />
          <h2 className="text-sm font-semibold text-foreground">We could not build your persona</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          We could not build your persona from the information we found. Upload your investment
          thesis and we will build it from that instead.
        </p>
        {onUploadThesis && (
          <CtaButton size="sm" className="mt-3" onClick={onUploadThesis}>
            Upload your thesis
          </CtaButton>
        )}
      </div>
    )
  }

  // none
  return (
    <div className="rounded-xl border border-border bg-gradient-to-r from-muted/50 via-muted/25 to-transparent px-6 py-5">
      <div className="flex items-center gap-2.5">
        <Sparkles className="size-4 text-muted-foreground/40" />
        <h2 className="text-sm font-semibold text-foreground">Create your investor persona</h2>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Upload your investment thesis below to build a personalized investor persona. This powers
        how founders are evaluated when they pitch to you.
      </p>
    </div>
  )
}

// --- SeedForge Link Section (always visible, disabled when no persona) ---

function SeedForgeLinkSection({
  slug,
  pitchLinkUrl,
  personaStatus,
  canEdit,
  onSlugSave,
}: {
  slug: string | null
  pitchLinkUrl: string
  personaStatus: PersonaStatus
  canEdit: boolean
  onSlugSave?: (slug: string) => Promise<string | undefined>
}) {
  const [copied, setCopied] = useState(false)
  const [editingSlug, setEditingSlug] = useState(false)
  const [slugDraft, setSlugDraft] = useState('')
  const [slugError, setSlugError] = useState<string | null>(null)
  const [slugSaving, setSlugSaving] = useState(false)

  const isDisabled = personaStatus !== 'ready'

  function handleCopy() {
    navigator.clipboard.writeText(pitchLinkUrl || '')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (editingSlug) {
    return (
      <div className="px-6 py-5 space-y-3">
        <div className="flex items-center gap-2.5">
          <Link2 className="size-4 text-[#f97316]" />
          <h2 className="text-sm font-semibold text-foreground">Edit your link</h2>
        </div>
        <div>
          <div className="flex items-center gap-0">
            <span className="rounded-l-lg border border-r-0 border-border bg-card/80 px-3 py-2 text-sm text-muted-foreground">
              {window.location.origin}/
            </span>
            <Input
              value={slugDraft}
              onChange={(e) => {
                setSlugDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                setSlugError(null)
              }}
              className={cn('rounded-l-none', slugError && 'border-red-500')}
              placeholder="your-slug"
              autoFocus
            />
          </div>
          {slugError && <p className="mt-1.5 text-xs text-red-400">{slugError}</p>}
          {!slugError && slugDraft.length > 0 && slugDraft.length < 3 && (
            <p className="mt-1.5 text-xs text-amber-400">Slug must be at least 3 characters</p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <SecondaryButton
            size="sm"
            icon={<X className="size-3.5" />}
            onClick={() => {
              setEditingSlug(false)
              setSlugError(null)
            }}
          >
            Cancel
          </SecondaryButton>
          <CtaButton
            size="sm"
            icon={<Check className="size-3.5" />}
            disabled={slugDraft.length < 3}
            loading={slugSaving}
            onClick={async () => {
              setSlugSaving(true)
              setSlugError(null)
              try {
                const error = await onSlugSave?.(slugDraft.trim())
                if (error) {
                  setSlugError(error)
                } else {
                  setEditingSlug(false)
                }
              } catch {
                setSlugError('This slug is already taken')
              } finally {
                setSlugSaving(false)
              }
            }}
          >
            {slugSaving ? 'Saving...' : 'Save'}
          </CtaButton>
        </div>
      </div>
    )
  }

  return (
    <EditOverlay
      editable={!isDisabled && canEdit && !!onSlugSave}
      onEdit={() => {
        setSlugDraft(slug ?? '')
        setEditingSlug(true)
      }}
    >
      <div className={cn('px-6 py-5', isDisabled && 'opacity-50 pointer-events-none')}>
        <div className="flex items-center gap-2.5">
          <Link2
            className={cn('size-4', isDisabled ? 'text-muted-foreground/40' : 'text-[#f97316]')}
          />
          <h2 className="text-sm font-semibold text-foreground">Your SeedForge link</h2>
          {isDisabled && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              Requires persona
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Share this link with founders so they can pitch directly to you
        </p>

        <div className="mt-4 flex items-center gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2">
            <Globe className="size-3.5 text-muted-foreground" />
            <span className="text-sm text-foreground">{pitchLinkUrl}</span>
          </div>
          <CtaButton
            size="sm"
            icon={copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            onClick={handleCopy}
            disabled={isDisabled}
          >
            {copied ? 'Copied!' : 'Copy link'}
          </CtaButton>
          {isDisabled ? (
            <SecondaryButton size="sm" icon={<ExternalLink className="size-3.5" />} disabled>
              Preview
            </SecondaryButton>
          ) : (
            <a
              href={pitchLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={secondaryLinkClasses({ size: 'sm' })}
            >
              <ExternalLink className="size-3.5" />
              <span>Preview</span>
            </a>
          )}
        </div>
      </div>
    </EditOverlay>
  )
}

// --- Thesis Dropzone ---

function ThesisDropzone({
  onDrop,
  onCancel,
}: {
  onDrop: (file: File) => void
  onCancel: () => void
}) {
  const handleDrop = useCallback(
    (accepted: File[]) => {
      const file = accepted[0]
      if (file) onDrop(file)
    },
    [onDrop]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
    onDropAccepted: handleDrop,
  })

  return (
    <div className="border-t border-[#f97316]/10 px-6 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
          Investment Thesis
        </p>
        <SecondaryButton size="sm" icon={<X className="size-3.5" />} onClick={onCancel}>
          Cancel
        </SecondaryButton>
      </div>
      <div
        {...getRootProps()}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 text-center transition-all',
          isDragActive
            ? 'border-[#f97316] bg-[#f97316]/5'
            : 'border-border/60 hover:border-[#f97316]/40 hover:bg-card/30'
        )}
      >
        <input {...getInputProps()} />
        <Upload className="size-5 text-muted-foreground/50" />
        <p className="text-xs text-muted-foreground">
          {isDragActive ? 'Drop your thesis here' : 'Drag & drop or click to upload'}
        </p>
        <p className="text-[10px] text-muted-foreground/40">PDF, DOC, DOCX or TXT up to 50 MB</p>
      </div>
    </div>
  )
}

// --- Main Component ---

export function InvestorDashboard({
  name,
  slug,
  pitchLinkUrl,
  persona,
  fundName,
  fundDescription,
  websiteUrl,
  investmentAreas,
  investmentGeographies,
  areaOptions = [],
  regionOptions = [],
  thesisFileName,
  editable = false,
  onProfileSave,
  onSlugSave,
  onThesisUpload,
}: InvestorDashboardProps) {
  const [editing, setEditing] = useState(false)
  const [editingThesis, setEditingThesis] = useState(false)
  const thesisRef = useRef<HTMLDivElement>(null)
  const openThesisUpload = () => {
    setEditingThesis(true)
    // The upload box is further down the page: bring it into view once it has rendered.
    setTimeout(() => thesisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
  }
  const firstName = name.split(' ')[0] || name
  const personaStatus = persona?.personaStatus ?? 'none'

  const canEdit = editable && !!onProfileSave

  function handleSave(data: InvestorProfileData) {
    onProfileSave?.(data)
    setEditing(false)
  }

  return (
    <div className="mx-auto max-w-3xl py-2">
      {/* Header */}
      <div className="mb-2 flex items-center gap-3">
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground/80">
          Investor Dashboard
        </p>
      </div>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        Welcome back, {firstName}
      </h1>

      {/* Persona Status Card */}
      <section className="mt-8">
        <PersonaStatusCard
          personaStatus={personaStatus}
          personaBrief={persona?.personaBrief}
          onUploadThesis={onThesisUpload ? openThesisUpload : undefined}
        />
      </section>

      {/* SeedForge Link + Profile */}
      <section className="mt-4">
        <div className="overflow-hidden rounded-xl border border-[#f97316]/20 bg-gradient-to-r from-[#f97316]/[0.06] via-[#f97316]/[0.03] to-transparent">
          <SeedForgeLinkSection
            slug={slug}
            pitchLinkUrl={pitchLinkUrl}
            personaStatus={personaStatus}
            canEdit={canEdit}
            onSlugSave={onSlugSave}
          />

          {/* Profile summary — inline edit */}
          {editing ? (
            <ProfileEditForm
              fundName={fundName}
              fundDescription={fundDescription ?? ''}
              websiteUrl={websiteUrl ?? ''}
              investmentAreas={investmentAreas ?? []}
              investmentGeographies={investmentGeographies ?? []}
              areaOptions={areaOptions}
              regionOptions={regionOptions}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <EditOverlay editable={canEdit} onEdit={() => setEditing(true)}>
              <div className="border-t border-[#f97316]/10 px-6 py-4">
                <h3 className="text-base font-semibold text-foreground">{fundName}</h3>
                {fundDescription && (
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {fundDescription}
                  </p>
                )}
                {websiteUrl ? (
                  <a
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#f97316] hover:underline"
                  >
                    <Globe className="size-3.5" />
                    {(() => {
                      try {
                        return new URL(websiteUrl).hostname
                      } catch {
                        return websiteUrl
                      }
                    })()}
                  </a>
                ) : (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground/50">
                    <Globe className="size-3.5" />
                    No website added
                  </p>
                )}

                {investmentAreas && investmentAreas.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      Investment Focus
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {investmentAreas.map((area) => (
                        <Badge key={area} variant="secondary" size="md">
                          {areaOptions.find((o) => o.value === area)?.label ?? area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {investmentGeographies && investmentGeographies.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      Geographies
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {investmentGeographies.map((geo) => (
                        <Badge key={geo} variant="outline" size="md">
                          {regionOptions.find((o) => o.value === geo)?.label ?? geo}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </EditOverlay>
          )}

          {/* Investment Thesis */}
          <div ref={thesisRef}>
            {editingThesis ? (
              <ThesisDropzone
                onDrop={(file) => {
                  onThesisUpload?.(file)
                  setEditingThesis(false)
                }}
                onCancel={() => setEditingThesis(false)}
              />
            ) : (
              <EditOverlay editable={!!onThesisUpload} onEdit={() => setEditingThesis(true)}>
                <div className="border-t border-[#f97316]/10 px-6 py-4">
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                    Investment Thesis
                  </p>
                  {thesisFileName ? (
                    <div className="flex items-center gap-2">
                      <FileText className="size-3.5 text-muted-foreground" />
                      <span className="text-sm text-foreground">{thesisFileName}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No thesis uploaded yet</p>
                  )}
                </div>
              </EditOverlay>
            )}
          </div>
        </div>
      </section>

      <div className="pb-8" />
    </div>
  )
}
